// DASHBOARD PORTAL
// pages/Terms/TermsManagement.js

import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import {
  Divider,
  Box,
  Typography,
  Button,
  Alert,
  Modal,
  CircularProgress,
  Paper,
  Chip,
  useTheme,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Grid,
} from "@mui/material";
import DataTable from "react-data-table-component";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { format } from "date-fns";

// TermForm Component for Add/Edits
const TermForm = ({ term, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: term?.title || "",
    description: term?.description || "",
    is_active: term?.is_active ?? true,
  });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    setIsSubmitting(true);

    const data = new FormData();
    data.append("title", formData.title);
    data.append("description", formData.description);
    data.append("is_active", formData.is_active);
    if (file) {
      data.append("file", file);
    }

    try {
      if (term) {
        // Update
        await axiosInstance.put(`/api/terms/${term.id}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // Create
        await axiosInstance.post("/api/terms", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save term");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TextField
        fullWidth
        label="Title"
        name="title"
        value={formData.title}
        onChange={handleChange}
        margin="normal"
        required
      />

      <TextField
        fullWidth
        label="Description (Optional)"
        name="description"
        value={formData.description}
        onChange={handleChange}
        margin="normal"
        multiline
        rows={4}
      />

      <Box mt={2}>
        <Typography variant="body2" mb={1}>
          Document (PDF, DOCX, etc.)
        </Typography>
        <input
          type="file"
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,.txt"
        />
        {term?.file_url && !file && (
          <Typography variant="caption" display="block" mt={1}>
            Current file:{" "}
            <a
              href={term.file_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2" }}
            >
              View Document
            </a>
          </Typography>
        )}
      </Box>

      {term && (
        <FormControlLabel
          control={
            <Switch
              checked={formData.is_active}
              onChange={handleChange}
              name="is_active"
              color="primary"
            />
          }
          label="Active (Visible in user invitations)"
          sx={{ mt: 2 }}
        />
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={18} /> : <SaveIcon />}
        >
          {isSubmitting ? "Saving..." : "Save Term"}
        </Button>
      </Box>
    </Box>
  );
};

// Main TermsManagement Component
export default function TermsManagement() {
  const theme = useTheme();
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/terms");
      if (response.data.success) {
        setTerms(response.data.terms || []);
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load terms");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (term = null) => {
    setEditingTerm(term);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTerm(null);
  };

  const handleSave = () => {
    fetchTerms();
    handleCloseModal();
    setSuccess(editingTerm ? "Term updated successfully" : "Term created successfully");
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleDelete = async (termId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/terms/${termId}`);
      setTerms(terms.filter((t) => t.id !== termId));
      setSuccess("Term deleted successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete term");
    }
  };

  const customStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "#131b2b" : "#f8fafc",
        },
      },
    },
  };

  const columns = [
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
      wrap: true,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      wrap: true,
    },
    {
      name: "File",
      cell: (row) =>
        row.file_url ? (
          <a
            href={row.file_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1976d2" }}
          >
            <DescriptionIcon fontSize="small" /> View
          </a>
        ) : (
          <Typography variant="caption" color="text.secondary">
            None
          </Typography>
        ),
    },
    {
      name: "Status",
      cell: (row) => (
        <Chip
          label={row.is_active ? "Active" : "Inactive"}
          color={row.is_active ? "success" : "default"}
          size="small"
        />
      ),
      width: "120px",
    },
    {
      name: "Created",
      selector: (row) => format(new Date(row.created_on), "MMM d, yyyy"),
      sortable: true,
      width: "140px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Box display="flex" gap={1}>
          <IconButton size="small" color="primary" onClick={() => handleOpenModal(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.id, row.title)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      width: "120px",
    },
  ];

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "80%", md: "600px" },
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: 2,
    p: 4,
  };

  return (
    <Paper sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 }, mx: 'auto', maxWidth: '1200px', bgcolor: 'background.paper' }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
          <Typography variant="h5" component="h1" sx={{ mb: { xs: 0, sm: 3 }, fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} color="textPrimary">
            Terms of Use Management
          </Typography>
          <Divider sx={{ mb: 3 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          Add New Term
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box textAlign="center" py={5}>
          <CircularProgress />
          <Typography mt={2}>Loading terms...</Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={terms}
          pagination
          highlightOnHover
          pointerOnHover
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
          customStyles={customStyles}
          noDataComponent={
            <Box py={5}>
              <Typography color="text.secondary">
                No terms have been created yet. Click "Add New Term" to create one.
              </Typography>
            </Box>
          }
        />
      )}

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" color={'text.primary'}>
              {editingTerm ? "Edit Term" : "Add New Term"}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          <TermForm term={editingTerm} onClose={handleCloseModal} onSave={handleSave} />
        </Box>
      </Modal>
    </Paper>
  );
}